'use no memo';

import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, DataStatus, Form, showNotification} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {indexBy, isNil, reject} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetInvoiceV4Query,
  useGetInvoicingDocumentPaymentListQuery,
  useSendInvoiceMutation,
} from '@dms/api/finance';
import {PaymentInfoResponseBody} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, useCurrencies, usePermissions} from '@dms/shared';

import {buildArray, useRequiredParams} from 'shared/web';

import {InvoiceActivityLog} from '../../components/InvoiceActivityLog/InvoiceActivityLog';
import {InvoiceDetailHeader} from '../../components/InvoiceDetailHeader';
import {InvoiceDetailSubHeader} from '../../components/InvoiceDetailSubHeader';
import {PaymentsListDetail} from '../../components/PaymentsListDetail';
import {
  type SectionItem,
  SectionsWithCenteredContent,
} from '../../components/SectionsWithCenteredContent';
import {invoicePaymentOptions} from '../../constants';
import {InvoiceVatRateProvider} from '../../context/InvoiceVatRateProvider';
import {useDocumentActions} from '../../hooks/useDocumentActions';
import {AccountingDocumentFormValues} from '../../types';
import {getDocumentSourceFlags} from '../../utils/getDocumentSourceFlags';
import {getInvoiceCategoryFlagProps} from '../../utils/getInvoiceCategoryFlagProps';
import {getInvoiceStateFlagProps} from '../../utils/getInvoiceStateFlagProps';
import {getPaymentStateFlagProps} from '../../utils/getPaymentStateFlagProps';
import {getShouldDisplaySendButton} from '../../utils/getShouldDisplaySendButton';
import {InvoiceDetails} from './components/InvoiceDetails';

export function InvoiceDetail() {
  const {id: invoiceId} = useRequiredParams();

  const {getCurrencyName} = useCurrencies();
  const [sendInvoice, {isLoading: isSendingDocument}] = useSendInvoiceMutation();
  const [subheaderActions] = useDocumentActions('ISSUED', invoiceId);

  const [canSendInvoice] = usePermissions({
    permissionKeys: ['sendInvoice'],
  });

  const {
    data: invoice,
    isLoading: isLoadingInvoice,
    isError: isInvoiceError,
  } = useGetInvoiceV4Query({invoiceId});

  const {
    data: paymentList,
    isLoading: isLoadingPaymentList,
    isError: isPaymentListError,
  } = useGetInvoicingDocumentPaymentListQuery({
    invoicingDocumentType: 'invoice',
    invoicingDocumentId: invoiceId,
  });

  const bankAccountValue = isNotNilOrEmpty(invoice?.paymentInfo?.bankAccount)
    ? reject(isNil, [
        invoice?.paymentInfo?.bankAccount,
        invoice?.paymentInfo?.bankName,
        invoice?.currency,
      ]).join(', ')
    : null;

  const shouldDisplaySendButton = getShouldDisplaySendButton(invoice) && canSendInvoice;
  const paymentTypesByType = indexBy((item) => item.value, invoicePaymentOptions);
  const paymentMethodLabel = invoice
    ? (paymentTypesByType[invoice.paymentInfo.paymentMethod]
        .label as PaymentInfoResponseBody['paymentMethod'])
    : 'BANK_TRANSFER';

  const initialValues = invoice
    ? {
        ...invoice,
        paymentInfo: {
          ...invoice.paymentInfo,
          bankAccount: bankAccountValue,
          paymentMethod: paymentMethodLabel,
        },
        exchangeRateRatio: {
          ratio: invoice.exchangeRateRatio?.ratio?.toString() ?? null,
          amount: invoice.exchangeRateRatio?.amount?.toString() ?? null,
          currency: getCurrencyName(invoice.exchangeRateRatio?.currency),
          type: i18n.t('general.actions.recalculate'),
        },
      }
    : undefined;

  const isLoading = isLoadingInvoice || isLoadingPaymentList;
  const isError = isInvoiceError || isPaymentListError;

  const handleSendInvoice = () => {
    if (isNil(invoice)) {
      showNotification.error();
      return;
    }

    sendInvoice({invoiceId: invoice.id}).unwrap().catch(handleApiError);
  };

  const invoiceSubActions = buildArray<Action>().when(shouldDisplaySendButton, {
    type: 'button',
    title: i18n.t('general.actions.send'),
    onClick: handleSendInvoice,
    isLoading: isSendingDocument,
    variant: 'link',
    leftIcon: 'content/send',
  });

  const pageTitle =
    invoice?.invoiceCategory === 'internal'
      ? i18n.t('entity.invoice.labels.internalInvoiceNumber', {invoiceNumber: invoice?.number})
      : i18n.t('entity.invoice.labels.invoiceNumber', {invoiceNumber: invoice?.number});

  return (
    <Box backgroundColor="palettes.neutral.20.100" minHeight="100%">
      <DataStatus isLoading={isLoading} isError={isError} minHeight="80vh">
        <Helmet title={pageTitle} />

        <Show when={invoice}>
          {(invoice) => (
            <Form<AccountingDocumentFormValues> defaultValues={initialValues}>
              {(control) => (
                <InvoiceVatRateProvider control={control} listItemType="invoicing-documents">
                  <VStack>
                    <Box backgroundColor="general.white">
                      <InvoiceDetailHeader
                        title={pageTitle}
                        data-testid={testIds.accounting.invoiceDetail('header')}
                      />

                      <InvoiceDetailSubHeader
                        author={invoice.author}
                        actions={subheaderActions}
                        subActions={invoiceSubActions}
                        createdAt={invoice.createdAt}
                        data-testid={testIds.accounting.invoiceDetail('subHeader')}
                        flags={reject(isNil, [
                          getInvoiceStateFlagProps(invoice),
                          getPaymentStateFlagProps(invoice.paymentState),
                          getInvoiceCategoryFlagProps(invoice.invoiceCategory),
                        ])}
                        subFlags={getDocumentSourceFlags(invoice.documentSource)}
                      />
                    </Box>

                    <SectionsWithCenteredContent
                      data-testid="accounting-sections"
                      sections={buildArray<SectionItem>()
                        .add({
                          content: <InvoiceDetails control={control} invoice={invoice} />,
                          id: 'detail',
                          label: i18n.t('general.labels.details'),
                        })
                        .add({
                          content: (
                            <PaymentsListDetail
                              paymentItems={paymentList}
                              invoiceType="invoice"
                              data-testid={testIds.accounting.invoiceDetail('paymentList')}
                            />
                          ),
                          id: 'payments',
                          label: i18n.t('page.accounting.invoiceDetailPayments.title'),
                        })
                        // The history belongs to the automation-rules epic (RQ_18): it names the
                        // rule behind a change, so it ships behind the same flag as the rules.
                        .whenFeatureEnabled(featureFlags.ORCHESTRATION_MODULE, {
                          content: (
                            <InvoiceActivityLog
                              invoiceId={invoiceId}
                              data-testid={testIds.accounting.invoiceDetail('activityLog')}
                            />
                          ),
                          id: 'activity-log',
                          label: i18n.t('general.labels.activityLogs'),
                        })}
                    />
                  </VStack>
                </InvoiceVatRateProvider>
              )}
            </Form>
          )}
        </Show>
      </DataStatus>
    </Box>
  );
}
